import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap} from 'rxjs/operators';
import {HttpClientService} from '../http-client/http-client.service';
import {EnvironmentService} from '../environment/environment.service';
import {LocalStorageService} from '../local-storage/local-storage.service';
import {AppStateService} from 'commons';
import {PersonalService} from '../personal/personal.service';
import {FilterDto, ProductFilterElementDto} from './filter-dto';
import {OldFilter, ProductFilterElement} from '../../model/filter';
import {BusinessPartner} from '../../model/business-partner';


// Kontext für Filter
export enum FilterContext {
  ProductChange = 'ProductChange',
  Delivery = 'Delivery'
}

export const filterProductContextProductChangeKey = 'filterProductContextProductChange';
export const filterProductContextDeliveryKey = 'filterProductContextDelivery';
export const selectedFilterKey = 'selectedFilter';

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  // Observable, das die vom Backend geladenen möglichen Filter liefert.
  // Es wird einmalig beim Start geladen und per shareReplay zwischengespeichert.
  private readonly possibleFilters$: Observable<ProductFilterElement[]>;

  // Intern verwaltete ausgewählte Filter-IDs, getrennt nach Kontext
  private readonly selectedFilters: { [key in FilterContext]: BehaviorSubject<string[]> };

  constructor(
    private readonly httpClientService: HttpClientService,
    private readonly environmentService: EnvironmentService,
    private readonly localStorageService: LocalStorageService,
    private readonly appStateService: AppStateService,
    private readonly personalService: PersonalService
  ) {
    const filterUrl = `${this.environmentService.beUrl}filter`;

    // Local-Storage Migration:
    // Falls der neue Local-Storage-Key noch nicht existiert, prüfen wir, ob es den alten Key 'selectedFilter' gibt.
    if (!this.localStorageService.getObjectItem(filterProductContextProductChangeKey)) {
      const oldFilter = this.localStorageService.getObjectItem<OldFilter>(selectedFilterKey);
      if (oldFilter) {
        // Wir migrieren nur die Produkt-Filter: nur die IDs aus oldFilter.products sind relevant.
        const migratedIds = oldFilter.products.map(p => p.id);
        // Speichere die migrierten IDs unter beiden neuen Keys.
        this.localStorageService.setObjectItem(filterProductContextProductChangeKey, migratedIds);
        this.localStorageService.setObjectItem(filterProductContextDeliveryKey, migratedIds);
        // Lösche den alten Key
        this.localStorageService.removeItem(selectedFilterKey);
      }
    }

    this.selectedFilters = {
      [FilterContext.ProductChange]: new BehaviorSubject<string[]>(this.loadLocalStorage(FilterContext.ProductChange)),
      [FilterContext.Delivery]: new BehaviorSubject<string[]>(this.loadLocalStorage(FilterContext.Delivery))
    };

    this.possibleFilters$ = this.httpClientService.get<FilterDto>(filterUrl).pipe(
      // Wir erwarten, dass die Daten im response.body stehen
      map(response => response.body),
      map((filterDto: FilterDto | null) => {
        if (!filterDto || !Array.isArray(filterDto.products)) {
          return [];
        }
        // Übersetze das DTO in unser ViewModel (nur products relevant)
        return this.mapProductFilters(filterDto.products);
      }),
      catchError(() => {
        // Bei einem Fehler geben wir ein leeres Array zurück
        return of([]);
      }),
      shareReplay(1)
    );
  }

  private mapProductFilters(productDtos: ProductFilterElementDto[]): ProductFilterElement[] {

    const validFilters: ProductFilterElement[] = [];

    for (const p of productDtos) {

      if (!p) {
        continue;
      }

      const id = p.id;
      const name = p.name;
      const hknrs = p.associated_hknrs;

      // Validiere die benötigten Felder
      const isValidId = typeof id === 'string' && id.trim() !== '';
      const isValidName = typeof name === 'string' && name.trim() !== '';
      const isValidHknrs = Array.isArray(hknrs);

      if (isValidId && isValidName && isValidHknrs) {
        validFilters.push({
          id: id, // Sicher, da oben geprüft
          name: name, // Sicher, da oben geprüft
          selected: false, // Standardwert für das ViewModel
          associatedHknrs: [...hknrs] // Kopiere das (ggf. bereinigte) Array
        });
      }
    }

    return validFilters;
  }

  private loadLocalStorage(context: FilterContext): string[] {
    const key = this.getLocalStorageKey(context);
    const stored = this.localStorageService.getObjectItem<string[]>(key);
    return stored ?? [];
  }

  private saveLocalStorage(context: FilterContext, selectedIds: string[]): void {
    const key = this.getLocalStorageKey(context);
    this.localStorageService.setObjectItem(key, selectedIds);
  }

  private getLocalStorageKey(context: FilterContext): string {
    return context === FilterContext.ProductChange ? filterProductContextProductChangeKey : filterProductContextDeliveryKey;
  }

  // Wendet auf die Liste der möglichen Filter den gespeicherten Aktivierungszustand an.
  private applySelection(filters: ProductFilterElement[], selectedIds: string[]): ProductFilterElement[] {
    return filters.map(filterElement => ({
      ...filterElement,
      selected: selectedIds.includes(filterElement.id)
    }));
  }

  private areProductFiltersEqualByIdAndSelected(
    prev: ProductFilterElement[] | null | undefined,
    curr: ProductFilterElement[] | null | undefined
  ): boolean {
    // Direkter Vergleich (beide null/undefined oder gleiche Instanz)
    if (prev === curr) {
      return true;
    }

    // Prüfen, ob einer null/undefined ist, der andere aber nicht
    if (!prev || !curr) {
      return false;
    }

    // Schneller Längenvergleich
    if (prev.length !== curr.length) {
      return false;
    }

    // Leere Arrays mit gleicher Länge sind gleich
    if (prev.length === 0) {
      return true;
    }

    // Erstelle eine Map für schnellen Zugriff auf die Elemente des vorherigen Arrays
    const prevMap = new Map<string, boolean>();
    for (const filter of prev) {
      prevMap.set(filter.id, filter.selected);
    }

    // Vergleiche jedes Element des aktuellen Arrays mit der Map
    for (const currentFilter of curr) {
      const prevSelected = prevMap.get(currentFilter.id);

      // Prüfe, ob die ID im vorherigen Array existierte UND
      // ob der 'selected'-Status übereinstimmt.
      if (prevSelected === undefined || prevSelected !== currentFilter.selected) {
        return false; // Element nicht gefunden oder 'selected' anders -> Arrays sind unterschiedlich
      }
    }

    // Wenn alle Elemente gefunden wurden und übereinstimmen, sind die Arrays gleich
    return true;
  }

  /**
   * Liefert ein Observable, das die möglichen Filter unabhängig vom Aktivierungszustand,
   * Kontext und Applikationszustand bereitstellt.
   */
  getPossibleFilters(): Observable<ProductFilterElement[]> {
    return this.possibleFilters$;
  }

  /**
   * Liefert ein Observable, das die anzuzeigenden Filter (inklusive Aktivierungszustand)
   * für den übergebenen Kontext (ProductChange oder Delivery) bereitstellt.
   */
  getProductFilters(context: FilterContext): Observable<ProductFilterElement[]> {
    // Je nach Kontext den entsprechenden personalisierten Zustand abfragen.
    const personalContext$: Observable<boolean> =
      context === FilterContext.ProductChange
        ? this.appStateService.isPersonalProductChangeFilter$
        : this.appStateService.isPersonalDeliveryFilter$;

    return combineLatest([
      this.possibleFilters$,
      this.selectedFilters[context].asObservable(),
      this.appStateService.isPersonalized$,
      personalContext$,
      // Damit der Stream auch startet, wenn noch kein aktiver BusinessPartner vorhanden ist.
      this.personalService.activeBusinessPartner$.pipe(startWith(null))
    ]).pipe(
      switchMap(([possibleFilters, storedSelectedIds, isPersonalized, isPersonalContext, activeBusinessPartner]) => {
        // Falls personalisierte Filter aktiv sind und ein aktiver BusinessPartner vorliegt,
        // wird die Filterliste anhand der Vertragsdaten eingeschränkt.
        const usePersonalizedFilters = isPersonalized && isPersonalContext && !!activeBusinessPartner?.businessPartnerId;
        if (usePersonalizedFilters) {
          return this.getPersonalizedProductFilters(activeBusinessPartner, possibleFilters, storedSelectedIds);
        } else {
          // Ansonsten alle Filter anzeigen.
          return this.getNonPersonalizedProductFilters(possibleFilters, storedSelectedIds);
        }
      }),
      // Vermeide unnötige Updates im Observable, wenn sich nichts ändert.
      distinctUntilChanged((prev: ProductFilterElement[], curr: ProductFilterElement[]) => {
        return this.areProductFiltersEqualByIdAndSelected(prev, curr);
      })
    );
  }

  private getNonPersonalizedProductFilters(possibleFilters: ProductFilterElement[],
                                           storedSelectedIds: string[]): Observable<ProductFilterElement[]> {
    return of(this.applySelection(possibleFilters, storedSelectedIds));
  }

  private getPersonalizedProductFilters(activeBusinessPartner: BusinessPartner,
                                        possibleFilters: ProductFilterElement[],
                                        storedSelectedIds: string[]): Observable<ProductFilterElement[]> {
    // falls keine associatedHknrs ODER (-1: immer anzeigen) -> Filter anzeigen
    // falls (-2: nie anzeigen) -> trotzdem anzeigen, da es Programmänderungsdokus gibt
    const showAlways = -1;
    const showNever = -2;

    return this.personalService.getBusinessPartnerFilterHKNRs(activeBusinessPartner.businessPartnerId).pipe(
      map((hkNumbers: number[]) => {
        const restrictedFilters = possibleFilters.filter(filter =>
          filter.associatedHknrs.some(hknr => hknr === showAlways || hknr === showNever ||  hkNumbers.includes(hknr))
        );
        return this.applySelection(restrictedFilters, storedSelectedIds);
      }),
      // Fehler beim HKNR-Abruf abfangen und nicht-personalisierte Filter anzeigen
      catchError(() => {
        return of(this.applySelection(possibleFilters, storedSelectedIds)); // Fallback: Alle Filter anzeigen
      })
    );
  }

  /**
   * Liefert ein Observable, das die aktivierten Filter
   * für den übergebenen Kontext (ProductChange oder Delivery) bereitstellt.
   */
  getSelectedProductFilter(context: FilterContext): Observable<ProductFilterElement[]> {
    return this.getProductFilters(context).pipe(
      map(filters => filters.filter(f => f.selected)),
      // Vermeide unnötige Updates im Observable, wenn sich nichts ändert.
      distinctUntilChanged((prev: ProductFilterElement[], curr: ProductFilterElement[]) => {
        return this.areProductFiltersEqualByIdAndSelected(prev, curr);
      })
    );
  }

  /**
   * Wird von Komponenten aufgerufen, wenn der Anwender den Aktivierungszustand ändert.
   * Aktualisiert intern den Zustand und speichert diesen (sofern es zu einer tatsächlichen Änderung kommt)
   * im Local Storage für den entsprechenden Kontext.
   */
  updateProductFilters(context: FilterContext, newFilters: ProductFilterElement[]): void {
    const newSelectedIds = newFilters.filter(f => f.selected).map(f => f.id);
    const currentSelectedIds = this.selectedFilters[context].getValue();

    // Erstelle Kopien und sortiere diese für den Vergleich
    const sortedNew = [...newSelectedIds].sort((a, b) => a.localeCompare(b));
    const sortedCurrent = [...currentSelectedIds].sort((a, b) => a.localeCompare(b));

    // Nur aktualisieren, wenn sich der Zustand tatsächlich ändert
    if (JSON.stringify(sortedNew) !== JSON.stringify(sortedCurrent)) {
      this.selectedFilters[context].next(newSelectedIds);
      this.saveLocalStorage(context, newSelectedIds);
    }
  }
}
